import {FC, useCallback, useContext, useEffect, useState} from "react";
import {useParams} from "react-router-dom";

import ErrorView, {ErrorViewType} from "common/components/error/ErrorView";
import LoadingSpinner from "common/components/spinner";
import {getEntityPortalStatusData} from "common/components/toggleDeactivationButton/getEntityPortalStatusData";
import {getDriverName} from "common/constants/driverName";
import {FetchStatus, useFetch} from "common/hooks/useFetch";
import {Api} from "common/services/apiProvider";
import {getDocumentTitle} from "common/util/DocumentTitleUtil";
import {AccountContextProvider} from "features/account/accountStateProvider";
import {useReactRouterLinkForBreadcrumb} from "@fleet/common/hooks/useBreadcrumbRenderItem";
import {UnreachableCode} from "@fleet/common/utils/UnreachableCode";

import {FleetOwnerPortalService} from "@bolteu/bolt-server-api-fleet-owner-portal";
import {Breadcrumb} from "@bolteu/kalep-react";

import {useBreadcrumbs} from "../hooks/useBreadcrumbs";
import DriverProfileBody from "./DriverProfileBody";
import EntityPortalStatus = FleetOwnerPortalService.EntityPortalStatus;

const getDriverFetchFunction = (api: Api, query: FleetOwnerPortalService.GetDriverRequest) =>
    api.fleetOwnerPortal.getDriver(query);

const DriverProfile: FC = () => {
    const fleetId = useContext(AccountContextProvider)?.getFleet()?.id;
    const params = useParams();
    const breadcrumbs = useBreadcrumbs(fleetId);

    const [driver, setDriver] = useState<FleetOwnerPortalService.GetDriverResponse | null>(null);

    const {status, data, fetch} = useFetch(getDriverFetchFunction);

    const fetchDriver = useCallback(() => {
        const driverId = Number(params.id);
        if (!fetch || !driverId || !fleetId) {
            return;
        }

        fetch({id: driverId});
    }, [fetch, params.id, fleetId]);

    const toggleDriverDeactivationStatus = useCallback(
        (newPortalStatus: EntityPortalStatus) => {
            if (driver) {
                setDriver({
                    ...driver,
                    ...getEntityPortalStatusData(newPortalStatus),
                });
            }
        },
        [driver],
    );

    useEffect(() => {
        if (status === FetchStatus.Success) {
            setDriver(data);
        }
    }, [status, data]);

    useEffect(() => {
        fetchDriver();
    }, [fetchDriver]);

    useEffect(() => {
        if (status === FetchStatus.Success && driver) {
            document.title = getDocumentTitle(getDriverName(driver.first_name, driver.last_name, driver.email));
        }
    }, [status, driver]);

    let Body: React.ReactNode = null;
    switch (status) {
        case FetchStatus.Init:
        case FetchStatus.Loading:
            Body = <LoadingSpinner show showLoadingText parentClassName="mt-9" />;
            break;
        case FetchStatus.Error:
            Body = <ErrorView type={ErrorViewType.NotFound} />;
            break;
        case FetchStatus.Success:
            if (driver) {
                Body = (
                    <DriverProfileBody
                        driver={driver}
                        toggleDriverDeactivationStatus={toggleDriverDeactivationStatus}
                    />
                );
            }
            break;
        default:
            UnreachableCode.never(status);
    }

    return (
        <div className="container mx-auto flex h-full w-full flex-col px-6 py-8">
            <Breadcrumb items={breadcrumbs} renderItem={useReactRouterLinkForBreadcrumb} />
            {Body}
        </div>
    );
};

export default DriverProfile;
